import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { useBookmark } from 'hooks/useBookmark'
import { ArticleApi } from 'interfaces/types'

export function useHomeTrendingLastestTopics(props: {
  trendingArticles: ArticleApi[]
}) {
  const { trendingArticles } = props
  const {
    isLoggedIn,
    bookmarks,
    loadingBookmark,
    onClickBookmark,
  } = useBookmark()
  const [loading, setLoading] = useState(false)

  const trendingRef = useRef<ArticleApi[]>(trendingArticles || [])
  const [activeTab, setActiveTab] = useState<
    'trending' | 'lastest_topics' | 'most_read' | 'news_events' | string
  >('trending')
  const [articles, setArticles] = useState<Record<string, ArticleApi[]>>(
    () => ({
      trending: trendingArticles,
      lastest_topics: [],
      most_read: [],
      news_events: [],
    }),
  )

  const fetchHomeTrendingPosts = useCallback((segmented: string) => {
    setLoading(true)
    let path = PATHS.COMMON.GET_HOMEPAGE_POSTS
    const params: Record<string, string> = { type: '' }
    if (segmented === 'lastest_topics') {
      path = PATHS.ARTICLE.GET_HOMEPAGE_LATEST_POSTS
    }
    if (segmented === 'most_read') {
      path = PATHS.ARTICLE.GET_ARTICLE_DETAIL
      params['slug'] = 'most-read'
    }
    if (segmented === 'news_events') {
      params['type'] = 'medical-news-events'
    }

    callApiNew(path, { params })
      .then((res: any) => {
        if (!res?.data || res?.messages) {
          throw new Error(JSON.stringify(res))
        }
        trendingRef.current = res.data?.posts
        setArticles((prevArticles) => ({
          ...prevArticles,
          [segmented]: res.data?.posts,
        }))
      })
      .finally(() => setLoading(false))
  }, [])

  const trendingPosts = useMemo<ArticleApi[]>(() => {
    const trendings = articles?.[activeTab]?.length
      ? articles?.[activeTab]
      : trendingRef.current || []
    return trendings
  }, [articles, activeTab])

  const onChangeTab = useCallback(
    (segmented: string) => {
      setActiveTab(segmented)

      const $trendingLastestTopics = document.getElementById(
        'trending_lastest_topics',
      )
      if ($trendingLastestTopics) {
        const space = 32
        const navigationV2Height =
          document.getElementById('navigation-v2')?.offsetHeight || 0
        const sitewideHeight =
          document.getElementById('sitewide-container')?.offsetHeight || 0
        let navigationHeight = 64
        if (navigationV2Height && sitewideHeight) {
          navigationHeight = navigationV2Height - sitewideHeight + space
        }
        const top = $trendingLastestTopics.offsetTop
        setTimeout(() => {
          window.scrollTo({ top: top - navigationHeight, behavior: 'smooth' })
        }, 1)
      }

      if (articles?.[segmented] && articles[segmented]?.length) {
        return
      }
      fetchHomeTrendingPosts(segmented)
    },
    [articles.trending, articles.lastest_topics],
  )

  useEffect(() => {
    trendingRef.current = trendingArticles
    setArticles(() => ({
      trending: trendingArticles,
      lastest_topics: [],
      most_read: [],
      news_events: [],
    }))
    setActiveTab('trending')
  }, [trendingArticles])

  useEffect(() => {
    if (isLoggedIn && !loading) {
      trendingRef.current = trendingArticles
      setArticles({
        trending: [],
        lastest_topics: [],
        most_read: [],
        news_events: [],
      })
      fetchHomeTrendingPosts(activeTab)
    }
  }, [isLoggedIn])

  return useMemo(
    () => ({
      loading,
      setLoading,
      loadingBookmark,
      activeTab,
      setActiveTab,
      onChangeTab,
      bookmarks,
      trendingPosts,
      onClickBookmark,
    }),
    [
      loading,
      loadingBookmark,
      activeTab,
      onChangeTab,
      bookmarks,
      trendingPosts,
      onClickBookmark,
    ],
  )
}
